'use client';
import React, { useEffect, useRef, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import Image from 'next/image';

interface Update {
  id: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  image_url: string | null;
  is_published: boolean;
  published_at: string;
  created_at: string;
}

const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  announcement: { label: 'Announcement', color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  event: { label: 'Event', color: '#7B2FF7', bg: 'rgba(123,47,247,0.1)' },
  update: { label: 'Update', color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
  info: { label: 'Info', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
};

const MOCK_UPDATES: Update[] = [
  {
    id: '1',
    title: 'Conect Club App Launch — Coming Soon',
    summary: 'We are putting the finishing touches on the Conect Club mobile app. Get ready for a seamless experience.',
    content: 'We are thrilled to announce that the Conect Club mobile application is in its final stages of development. The app will bring all the exclusive features of your membership directly to your fingertips — event access, member verification, networking tools, and much more. Stay tuned for the official launch date announcement.',
    category: 'announcement',
    image_url: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=800&q=80',
    is_published: true,
    published_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '2',
    title: 'Exclusive Members-Only Event — March 2026',
    summary: 'Join us for an intimate evening of networking, music, and curated experiences exclusively for Conect Club members.',
    content: 'We are hosting our first exclusive members-only event this March. The evening will feature live music, curated food and drinks, and structured networking sessions designed to help you forge meaningful connections. Spaces are strictly limited to verified members.',
    category: 'event',
    image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80',
    is_published: true,
    published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '3',
    title: 'New Partner Benefits Added to Your Membership',
    summary: 'We have onboarded three new lifestyle partners offering exclusive discounts and perks to all Conect Club members.',
    content: 'Your membership just got even more valuable. We have partnered with three premium lifestyle brands to bring you exclusive discounts, priority bookings, and VIP access. These partnerships span dining, wellness, and travel.',
    category: 'update',
    image_url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    is_published: true,
    published_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '4',
    title: 'Membership Verification System Upgraded',
    summary: 'We have improved the membership verification process for a faster, more secure experience.',
    content: 'Based on member feedback, we have overhauled our membership verification system. The new system offers instant QR-code verification, a refreshed digital membership card design, and enhanced security features to protect your identity.',
    category: 'update',
    image_url: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80',
    is_published: true,
    published_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '5',
    title: 'Welcome to Conect Club — Our Story',
    summary: 'Learn about the vision behind Conect Club and what makes our community different.',
    content: 'Conect Club was founded on a simple but powerful idea: that the right connections can transform your life. We are not just a membership club — we are a curated community of purpose-driven individuals who believe in growing together.',
    category: 'info',
    image_url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80',
    is_published: true,
    published_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '6',
    title: 'How to Make the Most of Your Membership',
    summary: 'A quick guide to unlocking all the benefits available to Conect Club members.',
    content: 'As a Conect Club member, you have access to a wide range of exclusive benefits. Verify your membership, check partner offers, watch for upcoming events, and use your membership card at partner venues to unlock discounts and VIP access.',
    category: 'info',
    image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
    is_published: true,
    published_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function UpdatesPage() {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUpdates = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('updates')
          .select('*')
          .eq('is_published', true)
          .order('published_at', { ascending: false });

        if (error || !data || data.length === 0) {
          setUpdates(MOCK_UPDATES);
        } else {
          setUpdates(data);
        }
      } catch {
        setUpdates(MOCK_UPDATES);
      } finally {
        setLoading(false);
      }
    };

    fetchUpdates();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el) => el.classList.add('is-visible'));
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, [updates]);

  const categories = ['all', ...Array.from(new Set(updates.map((u) => u.category)))];

  const filtered = activeCategory === 'all' ? updates : updates.filter((u) => u.category === activeCategory);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="min-h-screen bg-void pt-24 pb-20">
        {/* Radial glow */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 20%, rgba(123,47,247,0.12) 0%, transparent 70%)' }}
        />

        <div ref={sectionRef} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          {/* Page header */}
          <div className="text-center mb-12 fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(123,47,247,0.4)] bg-[rgba(123,47,247,0.08)] mb-6">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#7B2FF7' }} />
              <span className="text-[11px] font-medium tracking-[0.15em] uppercase" style={{ color: '#7B2FF7' }}>
                Latest from Conect Club
              </span>
            </div>
            <h1 className="font-serif font-bold mb-4" style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)', color: 'var(--cream)' }}>
              News &{' '}
              <em className="not-italic" style={{ color: '#7B2FF7' }}>Updates</em>
            </h1>
            <p className="text-lg font-light max-w-xl mx-auto" style={{ color: 'rgba(240,230,211,0.5)' }}>
              Stay informed with the latest announcements, events, and community updates.
            </p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10 fade-up">
            {categories.map((cat) => {
              const cfg = cat !== 'all' ? CATEGORY_CONFIG[cat] : null;
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-[0.12em] uppercase transition-all duration-200"
                  style={{
                    background: isActive
                      ? cfg?.bg ?? 'rgba(123,47,247,0.15)'
                      : 'rgba(240,230,211,0.04)',
                    border: `1px solid ${isActive ? (cfg?.color ?? '#7B2FF7') + '60' : 'rgba(240,230,211,0.1)'}`,
                    color: isActive ? (cfg?.color ?? '#7B2FF7') : 'rgba(240,230,211,0.45)',
                  }}
                >
                  {cat === 'all' ? 'All' : CATEGORY_CONFIG[cat]?.label ?? cat}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#7B2FF7', borderTopColor: 'transparent' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24" style={{ color: 'rgba(240,230,211,0.4)' }}>
              No updates found in this category.
            </div>
          ) : (
            <>
              {/* Featured post */}
              {featured && (
                <div
                  className="fade-up mb-10 rounded-3xl overflow-hidden group cursor-pointer transition-all duration-300"
                  style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
                  onClick={() => setExpandedId(expandedId === featured.id ? null : featured.id)}
                >
                  <div className="grid md:grid-cols-2 gap-0">
                    {featured.image_url && (
                      <div className="relative h-64 md:h-auto overflow-hidden">
                        <Image
                          src={featured.image_url}
                          alt={featured.title}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-105"
                          sizes="(max-width: 768px) 100vw, 50vw"
                        />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, transparent 60%, #111124)' }} />
                      </div>
                    )}
                    <div className="p-8 md:p-10 flex flex-col justify-center">
                      {/* Category badge */}
                      {CATEGORY_CONFIG[featured.category] && (
                        <span
                          className="inline-flex self-start px-3 py-1 rounded-full text-[10px] font-semibold tracking-[0.12em] uppercase mb-4"
                          style={{
                            background: CATEGORY_CONFIG[featured.category].bg,
                            color: CATEGORY_CONFIG[featured.category].color,
                            border: `1px solid ${CATEGORY_CONFIG[featured.category].color}40`,
                          }}
                        >
                          {CATEGORY_CONFIG[featured.category].label}
                        </span>
                      )}
                      <h2 className="font-serif font-bold text-2xl md:text-3xl mb-3 leading-snug" style={{ color: 'var(--cream)' }}>
                        {featured.title}
                      </h2>
                      <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(240,230,211,0.55)' }}>
                        {featured.summary}
                      </p>
                      {expandedId === featured.id && (
                        <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(240,230,211,0.7)' }}>
                          {featured.content}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[11px]" style={{ color: 'rgba(240,230,211,0.3)' }}>
                          {formatDate(featured.published_at)}
                        </span>
                        <span className="text-[11px] font-medium" style={{ color: '#7B2FF7' }}>
                          {expandedId === featured.id ? 'Read less ↑' : 'Read more →'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rest of posts grid */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {rest.map((update, i) => {
                    const cfg = CATEGORY_CONFIG[update.category];
                    const isExpanded = expandedId === update.id;
                    return (
                      <div
                        key={update.id}
                        className={`fade-up delay-${Math.min((i % 3 + 1) * 100, 300)} rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300`}
                        style={{ background: '#111124', border: '1px solid rgba(240,230,211,0.08)' }}
                        onClick={() => setExpandedId(isExpanded ? null : update.id)}
                      >
                        {update.image_url && (
                          <div className="relative h-44 overflow-hidden">
                            <Image
                              src={update.image_url}
                              alt={update.title}
                              fill
                              className="object-cover transition-transform duration-500 group-hover:scale-105"
                              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            />
                            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #111124 0%, transparent 60%)' }} />
                          </div>
                        )}
                        <div className="p-6 flex flex-col gap-3">
                          {cfg && (
                            <span
                              className="inline-flex self-start px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.1em] uppercase"
                              style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}40` }}
                            >
                              {cfg.label}
                            </span>
                          )}
                          <h3 className="font-serif font-semibold text-base leading-snug" style={{ color: 'var(--cream)' }}>
                            {update.title}
                          </h3>
                          <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(240,230,211,0.5)' }}>
                            {isExpanded ? update.content : update.summary}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[11px]" style={{ color: 'rgba(240,230,211,0.3)' }}>
                              {formatDate(update.published_at)}
                            </span>
                            <span className="text-[11px] font-medium" style={{ color: '#7B2FF7' }}>
                              {isExpanded ? 'Read less ↑' : 'Read more →'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
